// @story: none
import { Component, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogRef } from '@angular/cdk/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { ZagIcon, NavConfigEntry, NavItemListComponent } from '@zag/ui';

/**
 * Mobile full-screen menu dialog for the control UI.
 * Displays navigation entries (primary items + sections) in a scrollable overlay.
 *
 * @example
 * ```typescript
 * this.dialog.open(CtrlMobileMenuDialogComponent, {
 *   ...
 *   data: { navEntries }
 * });
 * ```
 */
@Component({
  selector: 'ctrl-mobile-menu-dialog',
  imports: [RouterModule, TranslatePipe, ZagIcon, NavItemListComponent],
  template: `
    <div class="mobile-menu-dialog" role="dialog" aria-modal="true" aria-labelledby="ctrl-mobile-menu-title">
      <header class="mobile-menu-dialog__header">
        <h2 id="ctrl-mobile-menu-title">{{ 'navigation.mainNavigation' | translate }}</h2>
        <button type="button" class="mobile-menu-dialog__close" (click)="onClose()" [attr.aria-label]="'common.close' | translate">
          <span zagIcon="close" aria-hidden="true"></span>
        </button>
      </header>

      <nav class="mobile-menu-dialog__nav">
        <ul class="mobile-menu-dialog__list">
          <zag-nav-item-list [entries]="navEntries" [collapsed]="false" />
        </ul>
      </nav>
    </div>
  `,
  styleUrl: './mobile-menu-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'mobile-menu-dialog-host',
  },
})
export class CtrlMobileMenuDialogComponent {
  private readonly dialogRef = inject(DialogRef<void>);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly navEntries: NavConfigEntry[] = this.dialogRef.config.data?.navEntries ?? [];

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.dialogRef.close());
  }

  protected onClose(): void {
    this.dialogRef.close();
  }
}
